import { useCallback } from "react";
import { View } from "react-native";
import Animated from "react-native-reanimated";

import type { RemoteOutboundMessage } from "$modules/expo-tv-connect";
import { PlatformKeyCode } from "$modules/expo-tv-connect";
import type { Device } from "$modules/expo-tv-connect-remote";
import AdTag from "$src/core/components/atoms/AdTag/AdTag";
import Button from "$src/core/components/atoms/Button/Button";
import Image from "$src/core/components/atoms/Image/Image";
import Text from "$src/core/components/atoms/Text/Text";
import TvConnectDeviceCard from "$src/core/components/organisms/TvConnectDeviceCard/TvConnectDeviceCard";
import TvConnectRemoteDpad from "$src/core/components/organisms/TvConnectRemoteDpad/TvConnectRemoteDpad";
import { IMAGE_BLUR_DIMENSIONS } from "$src/core/constants/image";
import {
  hasTvConnectDpadSupport,
  TV_CONNECT_SEEK_STEP_S,
} from "$src/core/constants/tv-connect";
import type { ConnectedCapabilities } from "$src/core/contexts/tv-connect-remote";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import useTranslation from "$src/core/hooks/use-translation";
import useTvConnectRemotePlaybackState from "$src/core/hooks/use-tv-connect-remote-playback-state";
import Alerting from "$src/core/services/alerting";
import formatPlaybackTime from "$src/core/utils/player/format-playback-time";

import $, {
  IMAGE_DIMENSIONS,
  progressFillTransition,
} from "./TvConnectRemoteSheet.styles";

type Props = {
  device: Device;
  capabilities?: ConnectedCapabilities | null;
  onDisconnect: () => Promise<void>;
  onSend: (message: RemoteOutboundMessage) => Promise<void>;
};

const NowPlayingProgressBar = ({
  ratio,
  isLive = false,
}: {
  ratio: number;
  isLive?: boolean;
}) => {
  const targetRatio = isLive ? 1 : ratio;

  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={$.nowPlayingProgressTrack}
    >
      <Animated.View
        style={[
          $.nowPlayingProgressFill,
          progressFillTransition,
          { width: `${targetRatio * 100}%` },
        ]}
      />
    </View>
  );
};

const NowPlayingCard = ({
  withVolumeControl,
  onSend,
}: {
  withVolumeControl: boolean;
  onSend: (message: RemoteOutboundMessage) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { type, status, progress, duration, asset, muted, trailer } =
    useTvConnectRemotePlaybackState();

  const handleSend = useCallback(
    (message: RemoteOutboundMessage) => onSend(message),
    [onSend],
  );

  const progressRatio =
    type === "playback" && duration > 0
      ? Math.min(1, Math.max(0, progress / duration))
      : 0;

  const adTimeLeftLabel =
    type === "ad" ? formatPlaybackTime(Math.max(0, duration - progress)) : null;

  const handlePlay = useCallback(
    () =>
      handleSend({
        type: "buttonPress",
        payload: {
          key: PlatformKeyCode.Play,
        },
      }),
    [handleSend],
  );

  const handlePause = useCallback(
    () =>
      handleSend({
        type: "buttonPress",
        payload: {
          key: PlatformKeyCode.Pause,
        },
      }),
    [handleSend],
  );

  const handleStop = useCallback(
    () =>
      handleSend({
        type: "buttonPress",
        payload: {
          key: PlatformKeyCode.Stop,
        },
      }),
    [handleSend],
  );

  const handleToggleMute = useCallback(
    () =>
      handleSend({
        type: "volume",
        payload: { action: "mute" },
      }),
    [handleSend],
  );

  const handleUnmute = useCallback(
    () =>
      handleSend({
        type: "volume",
        payload: { action: "unmute" },
      }),
    [handleSend],
  );

  const handleSeekBackward = useCallback(
    () =>
      handleSend({
        type: "seek",
        payload: {
          startOffset: Math.max(0, (progress ?? 0) - TV_CONNECT_SEEK_STEP_S),
        },
      }),
    [handleSend, progress],
  );

  const handleSeekForward = useCallback(
    () =>
      handleSend({
        type: "seek",
        payload: {
          startOffset: Math.min(
            duration ?? Infinity,
            (progress ?? 0) + TV_CONNECT_SEEK_STEP_S,
          ),
        },
      }),
    [handleSend, duration, progress],
  );

  const placeholderCoverImage = useResponsiveImage(asset?.thumbnail, {
    dimensions: [IMAGE_BLUR_DIMENSIONS],
  });
  const responsiveCoverImage = useResponsiveImage(asset?.thumbnail, {
    dimensions: IMAGE_DIMENSIONS.cover,
  });

  if (type === "idle") return null;

  return (
    <View style={$.nowPlayingCard}>
      <View style={$.nowPlayingContent}>
        <View style={$.nowPlayingCover}>
          {/* This is a decorative image, so we don't need an accessibility label */}
          {/* eslint-disable-next-line triple-rn-a11y/accessibility-label */}
          <Image
            accessibilityRole="none"
            placeholder={placeholderCoverImage}
            placeholderContentFit="cover"
            source={responsiveCoverImage}
            contentFit="cover"
            style={$.nowPlayingCoverImage}
          />
        </View>

        <View style={$.nowPlayingBody}>
          <Text
            accessibilityRole="text"
            preset="span"
            numberOfLines={2}
            style={$.nowPlayingTitle}
          >
            {asset?.title}
          </Text>

          {!!trailer && (
            <Text accessibilityRole="text" preset="tiny" style={$.progressTime}>
              {t("DETAIL/TRAILER")}
            </Text>
          )}

          {type === "ad" ? (
            <View style={$.adRow}>
              <AdTag style={$.adTag} />
              {!!adTimeLeftLabel && (
                <Text
                  accessibilityRole="text"
                  preset="tiny"
                  style={$.progressTime}
                >
                  {adTimeLeftLabel}
                </Text>
              )}
            </View>
          ) : type === "playback" ? (
            <View style={$.progressRow}>
              <Text
                accessibilityRole="text"
                preset="tiny"
                style={$.progressTime}
              >
                {formatPlaybackTime(progress)}
              </Text>
              <Text
                accessibilityRole="text"
                preset="tiny"
                style={$.progressTime}
              >
                {formatPlaybackTime(duration)}
              </Text>
            </View>
          ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          type === "live" ? (
            <Text accessibilityRole="text" preset="tiny" style={$.progressTime}>
              {t("COMMON/LIVE")}
            </Text>
          ) : null}
        </View>
      </View>

      <View style={$.controls}>
        <View style={$.controlsSideStart}>
          {withVolumeControl && (
            <Button
              name="tv_remote_mute"
              preset="transparent"
              size="md"
              isRounded
              icon={muted ? "sound-muted" : "sound-on"}
              onPress={muted ? handleUnmute : handleToggleMute}
              accessibilityLabel={
                muted ? t("PLAYER/SOUND/ON") : t("PLAYER/SOUND/OFF")
              }
            />
          )}
        </View>

        <View style={$.transportControls}>
          <Button
            name="tv_remote_seek_backward"
            preset="transparent"
            isRounded
            icon="skip-backward"
            onPress={handleSeekBackward}
            isDisabled={type === "live" || type === "ad"}
            accessibilityLabel={t("TV_REMOTE/SEEK_BACK")}
          />

          <Button
            name="tv_remote_play"
            preset="primary"
            isRounded
            icon={status === "playing" ? "pause" : "play"}
            onPress={status === "playing" ? handlePause : handlePlay}
            accessibilityLabel={
              status === "playing" ? t("TV_REMOTE/PAUSE") : t("TV_REMOTE/PLAY")
            }
          />

          <Button
            name="tv_remote_seek_forward"
            preset="transparent"
            icon="skip-forward"
            accessibilityLabel={t("TV_REMOTE/SEEK_FORWARD")}
            onPress={handleSeekForward}
            isDisabled={type === "live" || type === "ad"}
            isRounded
          />
        </View>

        <View style={$.controlsSideEnd}>
          <Button
            name="tv_remote_stop"
            isRounded
            icon="close"
            onPress={handleStop}
            accessibilityLabel={t("TV_REMOTE/STOP")}
          />
        </View>
      </View>

      <NowPlayingProgressBar ratio={progressRatio} isLive={type === "live"} />
    </View>
  );
};

const TvConnectRemoteSheet = ({
  device,
  capabilities,
  onDisconnect,
  onSend,
}: Props) => {
  const { t } = useTranslation();

  const handleSend = useCallback(
    (message: RemoteOutboundMessage) => onSend(message),
    [onSend],
  );

  const withDpad = hasTvConnectDpadSupport(capabilities?.supportedKeys);
  const withVolumeControl = capabilities?.volumeControl ?? false;

  const handleDisconnect = useCallback(() => {
    Alerting.prompt(
      t("TV_CONNECT/DISCOVERY/DISCONNECT_CONFIRM", {
        name: device.name ?? "TV",
      }),
      { onConfirm: onDisconnect },
    );
  }, [device.name, onDisconnect, t]);

  return (
    <View style={$.content}>
      <View style={$.panel}>
        <TvConnectDeviceCard device={device} onDisconnect={handleDisconnect} />

        <NowPlayingCard
          withVolumeControl={withVolumeControl}
          onSend={handleSend}
        />
      </View>

      <TvConnectRemoteDpad showDpad={withDpad} onSend={handleSend} />
    </View>
  );
};

export default TvConnectRemoteSheet;
